import { KeyboardDatePicker } from '@material-ui/pickers';
import { isValid, parse } from 'date-fns';
import { ClipboardEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

const PASTE_DATE_FORMATS = [
  'd MMM yyyy',
  'dd MMM yyyy',
  'd MMMM yyyy',
  'dd MMMM yyyy',
  'd-MMM-yyyy',
  'dd-MMM-yyyy',
  'd-MMMM-yyyy',
  'dd-MMMM-yyyy',
  'dd/MM/yyyy',
  'd/M/yyyy',
  'yyyy-MM-dd',
  'yyyy/MM/dd',
  'dd-MM-yyyy',
  'd-M-yyyy',
  'MMM d, yyyy',
  'MMMM d, yyyy',
  'MMM d yyyy',
  'd.M.yyyy',
  'dd.MM.yyyy',
];

export function parsePastedDate(text: string): Date | null {
  const trimmed = (text || '').trim().replace(/^["']|["']$/g, '');
  if (!trimmed) return null;

  for (const fmt of PASTE_DATE_FORMATS) {
    const parsed = parse(trimmed, fmt, new Date());
    if (isValid(parsed)) {
      return withTwoDigitYear(parsed);
    }
  }

  const native = new Date(trimmed);
  return isValid(native) ? withTwoDigitYear(native) : null;
}

function withTwoDigitYear(date: Date) {
  if (date.getFullYear() < 100) {
    date.setFullYear(2000 + date.getFullYear());
  }
  return date;
}

export const isCompleteValidDate = (value: unknown): boolean =>
  value instanceof Date && isValid(value);

export const ReleaseDateField = ({ name = 'release_date', control: propControl, ...props }) => {
  const form = useFormContext();
  const control = propControl || form.control;
  const fieldError = form.errors?.[name];

  return (
    <Controller
      name={name}
      control={control}
      render={({ value, onChange, onBlur }) => {
        const invalid = Boolean(value) && !isCompleteValidDate(value);
        const errorMessage =
          fieldError?.message || (invalid ? 'Enter a valid date as DD/MM/YYYY' : undefined);

        const applyPastedDate = (event: ClipboardEvent<HTMLInputElement>) => {
          const parsed = parsePastedDate(event.clipboardData.getData('text'));
          if (!parsed) return;
          event.preventDefault();
          event.stopPropagation();
          onChange(parsed);
        };

        return (
          <KeyboardDatePicker
            style={{ marginTop: 0, marginBottom: 0 }}
            inputVariant="outlined"
            variant="inline"
            name={name}
            format="dd/MM/yyyy"
            margin="normal"
            id="release-date-picker"
            label="Release Date/Air Date"
            invalidDateMessage="Enter a valid date as DD/MM/YYYY"
            autoOk
            fullWidth
            size="small"
            {...props}
            helperText={errorMessage || 'Release date or Air date of first episode.'}
            error={Boolean(errorMessage)}
            value={value || null}
            onChange={onChange}
            onBlur={onBlur}
            inputProps={{
              ...(props.inputProps || {}),
              onPaste: applyPastedDate,
            }}
          />
        );
      }}
    />
  );
};
